import React from 'react';

const UpdatedByModal = ({ isOpen, onClose, item, darkMode = false }) => {
  if (!isOpen || !item) return null;

  const formatDate = (date) => {
    return new Date(date).toLocaleString('en-IN', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      hour12: true
    });
  };

  const formatValue = (value, field = '') => {
    if (value === null || value === undefined) return 'N/A';
    
    // Handle object IDs for brand and category
    if (field === 'brand' || field === 'category') {
      if (typeof value === 'string') {
        // This is an ID, try to find the name from current item data
        if (field === 'brand' && item.brand && item.brand._id === value) {
          return item.brand.name;
        }
        if (field === 'category' && item.category && item.category._id === value) {
          return item.category.name;
        }
        return `ID: ${value}`;
      }
      if (typeof value === 'object' && value.name) {
        return value.name;
      }
      return JSON.stringify(value);
    }
    
    if (typeof value === 'object') return JSON.stringify(value, null, 2);
    if (typeof value === 'number') return value.toLocaleString();
    return value.toString();
  };

  const getFieldDisplayName = (field) => {
    const fieldNames = {
      'skuDescription': 'Description',
      'brand': 'Brand',
      'category': 'Category',
      'series': 'Series',
      'hsnCode': 'HSN Code',
      'gstRate': 'GST Rate',
      'mrp': 'MRP',
      'offerType.nrp': 'Offer - NRP',
      'offerType.clp': 'Offer - CLP',
      'offerType.kap': 'Offer - KAP',
      'offerType.sdp': 'Offer - SDP',
      'offerType.hvr': 'Offer - HVR',
      'offerType.spl': 'Offer - SPL',
      'offerType.flat': 'Offer - Flat'
    };
    return fieldNames[field] || field.charAt(0).toUpperCase() + field.slice(1);
  };

  const getChangesByUpdate = (updatedByEntry) => {
    if (!updatedByEntry.pastData) return [];
    
    const changes = [];
    const currentData = item;
    const pastData = updatedByEntry.pastData;
    
    // Compare fields to identify changes
    const fieldsToCheck = [
      'skuDescription', 'brand', 'category', 'series', 'hsnCode', 
      'gstRate', 'mrp', 'offerType'
    ];
    
    fieldsToCheck.forEach(field => {
      const currentValue = currentData[field];
      const pastValue = pastData[field];
      
      if (field === 'offerType') {
        // Special handling for offerType object
        if (pastValue && currentValue) {
          Object.keys(pastValue).forEach(offerKey => {
            if (pastValue[offerKey] !== currentValue[offerKey]) {
              changes.push({
                field: `offerType.${offerKey}`,
                from: pastValue[offerKey],
                to: currentValue[offerKey],
                fieldName: getFieldDisplayName(`offerType.${offerKey}`)
              });
            }
          });
        }
      } else {
        if (JSON.stringify(pastValue) !== JSON.stringify(currentValue)) {
          changes.push({
            field,
            from: pastValue,
            to: currentValue,
            fieldName: getFieldDisplayName(field)
          });
        }
      }
    });
    
    return changes;
  };

  return (
    <div className={`fixed inset-0 flex items-center justify-center z-50 backdrop-blur-md transition-all duration-300 ${
      darkMode ? 'bg-black/70' : 'bg-black/50'
    }`}>
      <div className={`rounded-2xl max-w-4xl w-full mx-4 max-h-[90vh] overflow-hidden shadow-2xl border transition-all duration-300 ${
        darkMode 
          ? 'bg-gradient-to-br from-gray-800 via-gray-800 to-gray-900 border-gray-700' 
          : 'bg-gradient-to-br from-white via-white to-gray-50 border-gray-200'
      }`}>
        <div className={`p-6 border-b transition-colors duration-300 ${
          darkMode 
            ? 'border-gray-700 bg-gradient-to-r from-indigo-900 via-purple-900 to-pink-900' 
            : 'border-gray-200 bg-gradient-to-r from-blue-600 to-indigo-600'
        }`}>
          <div className="flex justify-between items-center">
            <div>
              <h3 className="text-xl font-bold text-white drop-shadow-lg">
                Update History
              </h3>
              <p className={`text-sm mt-1 ${
                darkMode ? 'text-indigo-200' : 'text-blue-100'
              }`}>
                {item.skuCode} - {item.skuDescription}
              </p>
            </div>
            <button
              onClick={onClose}
              className={`p-2 rounded-full transition-all duration-200 hover:scale-110 ${
                darkMode 
                  ? 'text-indigo-200 hover:text-white hover:bg-gray-700/50' 
                  : 'text-blue-100 hover:text-white hover:bg-blue-700/50'
              }`}
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>
        
        <div className={`p-6 overflow-y-auto max-h-[calc(90vh-120px)] ${
          darkMode ? 'scrollbar-dark' : 'scrollbar-light'
        }`}>
          {/* Current Item Info */}
          <div className={`mb-6 p-5 rounded-xl border transition-colors duration-300 ${
            darkMode
              ? 'bg-gradient-to-r from-indigo-900/30 to-purple-900/30 border-indigo-700/50'
              : 'bg-gradient-to-r from-blue-50 to-indigo-50 border-blue-200'
          }`}>
            <h4 className={`font-semibold mb-3 flex items-center transition-colors duration-300 ${
              darkMode ? 'text-indigo-300' : 'text-blue-900'
            }`}>
              <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              Current Item Information
            </h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
              <div className={`p-3 rounded-lg border transition-colors duration-300 ${
                darkMode 
                  ? 'bg-gray-700 border-gray-600' 
                  : 'bg-white border-gray-200'
              }`}>
                <span className={`font-medium ${
                  darkMode ? 'text-gray-300' : 'text-gray-600'
                }`}>SKU Code:</span> 
                <div className={`font-semibold mt-1 ${
                  darkMode ? 'text-gray-100' : 'text-gray-900'
                }`}>{item.skuCode}</div>
              </div>
              <div className={`p-3 rounded-lg border transition-colors duration-300 ${
                darkMode 
                  ? 'bg-gray-700 border-gray-600' 
                  : 'bg-white border-gray-200'
              }`}>
                <span className={`font-medium ${
                  darkMode ? 'text-gray-300' : 'text-gray-600'
                }`}>Description:</span> 
                <div className={`font-semibold mt-1 ${
                  darkMode ? 'text-gray-100' : 'text-gray-900'
                }`}>{item.skuDescription}</div>
              </div>
              <div className={`p-3 rounded-lg border transition-colors duration-300 ${
                darkMode 
                  ? 'bg-gray-700 border-gray-600' 
                  : 'bg-white border-gray-200'
              }`}>
                <span className={`font-medium ${
                  darkMode ? 'text-gray-300' : 'text-gray-600'
                }`}>Brand:</span> 
                <div className={`font-semibold mt-1 ${
                  darkMode ? 'text-gray-100' : 'text-gray-900'
                }`}>{item.brand?.name || 'N/A'}</div>
              </div>
              <div className={`p-3 rounded-lg border transition-colors duration-300 ${
                darkMode 
                  ? 'bg-gray-700 border-gray-600' 
                  : 'bg-white border-gray-200'
              }`}>
                <span className={`font-medium ${
                  darkMode ? 'text-gray-300' : 'text-gray-600'
                }`}>Category:</span> 
                <div className={`font-semibold mt-1 ${
                  darkMode ? 'text-gray-100' : 'text-gray-900'
                }`}>{item.category?.name || 'N/A'}</div>
              </div>
              <div className={`p-3 rounded-lg border transition-colors duration-300 ${
                darkMode 
                  ? 'bg-gray-700 border-gray-600' 
                  : 'bg-white border-gray-200'
              }`}>
                <span className={`font-medium ${
                  darkMode ? 'text-gray-300' : 'text-gray-600'
                }`}>MRP:</span> 
                <div className={`font-semibold mt-1 ${
                  darkMode ? 'text-emerald-400' : 'text-green-600'
                }`}>₹{item.mrp?.toLocaleString() || 'N/A'}</div>
              </div>
              <div className={`p-3 rounded-lg border transition-colors duration-300 ${
                darkMode 
                  ? 'bg-gray-700 border-gray-600' 
                  : 'bg-white border-gray-200'
              }`}>
                <span className={`font-medium ${
                  darkMode ? 'text-gray-300' : 'text-gray-600'
                }`}>Last Updated:</span> 
                <div className={`font-semibold mt-1 ${
                  darkMode ? 'text-gray-100' : 'text-gray-900'
                }`}>{formatDate(item.lastUpdatedAt)}</div>
              </div>
            </div>
          </div>

          {/* Update History */}
          {item.updatedBy && item.updatedBy.length > 0 ? (
            <div className="space-y-6">
              <h4 className={`font-semibold mb-4 flex items-center transition-colors duration-300 ${
                darkMode ? 'text-gray-100' : 'text-gray-900'
              }`}>
                <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                Update History ({item.updatedBy.length} {item.updatedBy.length === 1 ? 'update' : 'updates'})
              </h4>
              
              {item.updatedBy
                .sort((a, b) => new Date(b.date) - new Date(a.date))
                .map((update, index) => {
                  const changes = getChangesByUpdate(update);
                  
                  return (
                    <div key={index} className={`border rounded-xl p-5 hover:shadow-lg transition-all duration-300 ${
                      darkMode
                        ? 'border-gray-600 bg-gradient-to-r from-gray-700 to-gray-800 hover:border-gray-500'
                        : 'border-gray-200 bg-gradient-to-r from-gray-50 to-gray-100 hover:border-gray-300'
                    }`}>
                      <div className="flex justify-between items-start mb-4">
                        <div className="flex-1">
                          <div className="flex items-center gap-3 mb-2">
                            <div className={`px-3 py-1 rounded-full text-sm font-medium transition-colors duration-300 ${
                              darkMode
                                ? 'bg-indigo-900/50 text-indigo-300 border border-indigo-700'
                                : 'bg-blue-100 text-blue-800 border border-blue-200'
                            }`}>
                              Update #{item.updatedBy.length - index}
                            </div>
                            {changes.length === 0 ? (
                              <div className={`px-3 py-1 rounded-full text-sm transition-colors duration-300 ${
                                darkMode
                                  ? 'bg-gray-700 text-gray-300 border border-gray-600'
                                  : 'bg-gray-100 text-gray-600 border border-gray-200'
                              }`}>
                                No changes detected
                              </div>
                            ) : (
                              <div className={`px-3 py-1 rounded-full text-sm font-medium transition-colors duration-300 ${
                                darkMode
                                  ? 'bg-emerald-900/50 text-emerald-300 border border-emerald-700'
                                  : 'bg-green-100 text-green-800 border border-green-200'
                              }`}>
                                {changes.length} {changes.length === 1 ? 'change' : 'changes'}
                              </div>
                            )}
                          </div>
                          <div className={`text-sm mb-1 transition-colors duration-300 ${
                            darkMode ? 'text-gray-300' : 'text-gray-600'
                          }`}>
                            📅 {formatDate(update.date)}
                          </div>
                          {update.employee?.name && (
                            <div className={`text-sm transition-colors duration-300 ${
                              darkMode ? 'text-gray-300' : 'text-gray-600'
                            }`}>
                              👤 By: <span className="font-medium">{update.employee.name}</span>
                            </div>
                          )}
                        </div>
                      </div>

                      {/* Changes List */}
                      {changes.length > 0 ? (
                        <div className="space-y-3">
                          <h5 className={`text-sm font-semibold flex items-center transition-colors duration-300 ${
                            darkMode ? 'text-gray-200' : 'text-gray-700'
                          }`}>
                            <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v10a2 2 0 002 2h8a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                            </svg>
                            Changes Made:
                          </h5>
                          {changes.map((change, changeIndex) => (
                            <div key={changeIndex} className={`border rounded-lg p-4 shadow-sm transition-colors duration-300 ${
                              darkMode
                                ? 'bg-gray-600 border-gray-500'
                                : 'bg-white border-gray-200'
                            }`}>
                              <div className={`font-semibold mb-3 flex items-center transition-colors duration-300 ${
                                darkMode ? 'text-gray-100' : 'text-gray-800'
                              }`}>
                                <svg className={`w-4 h-4 mr-2 ${
                                  darkMode ? 'text-indigo-400' : 'text-blue-500'
                                }`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7h12m0 0l-4-4m4 4l-4 4m0 6H4m0 0l4 4m-4-4l4-4" />
                                </svg>
                                {change.fieldName || change.field.charAt(0).toUpperCase() + change.field.slice(1)}
                              </div>
                              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <div className={`border-l-4 pl-4 ${
                                  darkMode ? 'border-red-500' : 'border-red-400'
                                }`}>
                                  <div className={`font-semibold text-sm mb-2 flex items-center ${
                                    darkMode ? 'text-red-400' : 'text-red-700'
                                  }`}>
                                    <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 12H4" />
                                    </svg>
                                    Previous Value:
                                  </div>
                                  <div className={`border rounded-lg p-3 font-medium transition-colors duration-300 ${
                                    darkMode
                                      ? 'bg-red-900/30 border-red-700 text-red-200'
                                      : 'bg-red-50 border-red-200 text-red-900'
                                  }`}>
                                    {formatValue(change.from, change.field)}
                                  </div>
                                </div>
                                <div className={`border-l-4 pl-4 ${
                                  darkMode ? 'border-emerald-500' : 'border-green-400'
                                }`}>
                                  <div className={`font-semibold text-sm mb-2 flex items-center ${
                                    darkMode ? 'text-emerald-400' : 'text-green-700'
                                  }`}>
                                    <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                                    </svg>
                                    New Value:
                                  </div>
                                  <div className={`border rounded-lg p-3 font-medium transition-colors duration-300 ${
                                    darkMode
                                      ? 'bg-emerald-900/30 border-emerald-700 text-emerald-200'
                                      : 'bg-green-50 border-green-200 text-green-900'
                                  }`}>
                                    {formatValue(change.to, change.field)}
                                  </div>
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <div className={`border rounded-lg p-4 text-center transition-colors duration-300 ${
                          darkMode
                            ? 'bg-gray-700 border-gray-600'
                            : 'bg-gray-100 border-gray-200'
                        }`}>
                          <svg className={`w-8 h-8 mx-auto mb-2 ${
                            darkMode ? 'text-gray-500' : 'text-gray-400'
                          }`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                          </svg>
                          <div className={`text-sm font-medium ${
                            darkMode ? 'text-gray-300' : 'text-gray-600'
                          }`}>No Data Changes Detected</div>
                          <div className={`text-xs mt-1 ${
                            darkMode ? 'text-gray-400' : 'text-gray-500'
                          }`}>This update may have been an image upload or other non-tracked change</div>
                        </div>
                      )}

                      {/* Additional Update Info */}
                      {update.changedFields && update.changedFields.length > 0 && (
                        <div className={`mt-3 pt-3 border-t transition-colors duration-300 ${
                          darkMode ? 'border-gray-600' : 'border-gray-200'
                        }`}>
                          <div className={`text-xs transition-colors duration-300 ${
                            darkMode ? 'text-gray-300' : 'text-gray-600'
                          }`}>
                            <span className="font-medium">Fields modified:</span> {update.changedFields.join(', ')}
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })}
            </div>
          ) : (
            <div className={`text-center py-12 rounded-xl border-2 border-dashed transition-colors duration-300 ${
              darkMode
                ? 'bg-gradient-to-br from-gray-800 to-gray-900 border-gray-600'
                : 'bg-gradient-to-br from-gray-50 to-gray-100 border-gray-300'
            }`}>
              <svg className={`w-16 h-16 mx-auto mb-4 ${
                darkMode ? 'text-gray-500' : 'text-gray-400'
              }`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
              <div className="space-y-2">
                <h5 className={`text-lg font-semibold transition-colors duration-300 ${
                  darkMode ? 'text-gray-200' : 'text-gray-700'
                }`}>No Update History Available</h5>
                <p className={`text-sm max-w-md mx-auto transition-colors duration-300 ${
                  darkMode ? 'text-gray-300' : 'text-gray-600'
                }`}>
                  This item hasn't been modified since its creation. All data remains as originally entered.
                </p>
                <div className={`mt-4 p-3 rounded-lg inline-block transition-colors duration-300 ${
                  darkMode
                    ? 'bg-indigo-900/30 border border-indigo-700'
                    : 'bg-blue-50 border border-blue-200'
                }`}>
                  <p className={`text-xs ${
                    darkMode ? 'text-indigo-300' : 'text-blue-700'
                  }`}>
                    <span className="font-semibold">Created:</span> {formatDate(item.createdAt)}
                  </p>
                  {item.createdBy?.name && (
                    <p className={`text-xs ${
                      darkMode ? 'text-indigo-300' : 'text-blue-700'
                    }`}>
                      <span className="font-semibold">By:</span> {item.createdBy.name}
                    </p>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>

        <div className={`p-6 border-t transition-colors duration-300 ${
          darkMode
            ? 'border-gray-700 bg-gradient-to-r from-gray-700/40 to-gray-800/40'
            : 'border-gray-200 bg-gradient-to-r from-gray-50 to-gray-100'
        }`}>
          <div className="flex gap-3 justify-center">
            <button
              onClick={onClose}
              className={`py-3 px-8 rounded-lg font-semibold shadow-md hover:shadow-lg flex items-center transition-all duration-200 transform hover:scale-105 ${
                darkMode
                  ? 'bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-700 hover:to-purple-700 text-white'
                  : 'bg-blue-600 hover:bg-blue-700 text-white'
              }`}
            >
              <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
              Close History
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default UpdatedByModal;
